import _ from "lodash";
import moment from "moment";
import {
  createSelector,
} from "reselect";
import {
  IsoDateFormat,
} from "CommonCore/l10n";

export function makeGetId(id) {
  return _.isString(id) ? (e => e[id]) : id;
}

export function injectFunction(func, state) {
  if (func && func.generator) {
        // Parameter ist eine Generator-Beschreibung mit Injects {inject: ..., generator: ...}
    return injectGenerator(func.generator, func.inject, state);
  }
  if (!_.isFunction(func)) {
            // on-the-fly Function, die den Boolean returned und den Parameter ignoriert
    return function() {
      return func;
    };
  }
            // Function as-is liefern
  return func;
}


export function injectGenerator(generator, inject, state) {
  if (inject) {
    if (_.isString(inject)) {
      inject = [inject];
    }
    const injectArgs = inject.map(x => _.isFunction(x) ? x(state) : _.get(state, x));
    generator = generator.apply(null, injectArgs);
  }
  return generator;
}

export function defaultFromSchema(schema, injects, entity) {
  const result = {};
  schema.table.filter(prop => prop.hasOwnProperty("default"))
    .forEach(prop => {
      let gen = prop.default;
      if (gen.inject) {
        gen = injectGenerator(gen.generator, gen.inject, injects);
      }
      result[prop.field] = (_.isFunction(gen) ? gen(entity) : _.cloneDeep(gen));
    });
  return result;
}

function appendInjectsFromTableDef(injects, tableDef) {
  function isArrayElement(def) {
    return def.type === "array";
  }

  tableDef.forEach(def => {
    if (def) {
      const { inject } = def;
      if (_.isString(inject)) {
        injects[inject] = true;
      } else if (_.isArray(inject)) {
        inject.forEach(i => injects[i] = true);
      } else if (isArrayElement(def)) {
        appendInjectsFromTableDef(injects, def.items);
      } else if (def.subitems) {
                // Injects aus dynamisch generierten Subitems hinzufügen
        appendInjectsFromTableDef(injects, def.subitems);
      }
    }
  });
}

export function injectsFromSchema(schema) {
  const injects = {};

  const filterData = _.values(schema.filters);
  const allDefs = filterData.map(f => f.subquery)
    .concat(filterData.map(f => f.filterbox))
    .concat(schema.table)
    .concat(_.map(schema.table, f => f.default))
    .concat(_.map(schema.table, f => f.hideFromEditor))
    .concat(_.map(schema.table, f => f.hideFromDetail));
  appendInjectsFromTableDef(injects, allDefs);

  return _.keys(injects);
}

export function injectsFromSchemaAndRenderMethods(schema, renderMethods) {
  const schemaInjects = injectsFromSchema(schema);
  const renderMethodInjects = _.flatten(_.values(renderMethods).map(rm => rm.inject).filter(_.identity));
  return _.uniq(schemaInjects.concat(renderMethodInjects));
}

export function patchField(schema, data) {
    // Felddaten in Table ersetzen
  const fld = schema.table.find(x => x.field == data.field);
  if (fld) {
    _.assign(fld, data);
  }
}

export function patchFields(schema, array) {
    // Felddaten eines Array von Felder ersetzen
  _.map(array, item => patchField(schema, item));
}

export function createInjectsSelector(injects) {
  return createSelector(injects.map(path => props => {
    const split = path.split(".");
    return props[split[split.length - 1]];
  }), (...injectedValues) => {
    const state = {};
    injects.forEach((path, i) => _.set(state, path, injectedValues[i]));
    return state;
  });
}

export function defaultFilterCriterion(schema) {
  return schema.filterbox === "boolean"
? true
        : schema.filterbox === "text"
? ""
            : schema.filterbox === "date"
? { date: moment.utc().format(IsoDateFormat), comparison: ">" }
                : schema.filterbox === "number"
? { op: "=" }
                    : [];
}

export function defaultSort(schema) {
  return schema.defaultSort
        ? _.map(schema.defaultSort, s => ({ key: s.field, reverse: s.reverse }))
        : _.findKey(schema.sorting, opt => opt.defaultSorted);
}

export function schemaIndexText(collection, fields, state) {
  const textFieldNames = _.keys(fields).filter(f => {
    const fb = fields[f].filterbox;
    return !fb || fb === "text" || fb.choices;
  });

  const itemTerms = _.map(collection, item => {
    const terms = _.flatMap(textFieldNames, fieldName => {
      const desc = fields[fieldName];
      if (desc.subquery) {
        const { generator, inject, fields: subfields } = desc.subquery;
        let generated = injectGenerator(generator, inject, state)(item);
        if (_.isEmpty(generated)) {
          return;
        }
        if (!_.isArray(generated)) {
          generated = [generated];
        }

        return schemaIndexText(generated, subfields, state);
      }
      const value = item[desc.alias || fieldName];
      return _.isString(value)
? _.deburr(value).toLowerCase().split(" ")
                : _.isNumber(value)
? value.toString()
                    : void 0;
    }).filter(_.identity);
    return { item, terms };
  });

  const index = {};
  for (const { item, terms } of itemTerms) {
    for (const term of terms) {
      const dir = index[term] instanceof Function ? (index[term] = []) : index[term] || (index[term] = []);
      dir.push(item);
    }
  }

  return _.map(index, (items, term) => [term, items]);
}

function schemaSearchIndexMultiple(index, getId, searchTerms) {
  let found = {};
  let firstRound = true;
  for (let searchTerm of searchTerms) {
    searchTerm = _.deburr(searchTerm).toLowerCase();
    const termFound = firstRound ? found : {};
    for (const [term, items] of index) {
      if (term.indexOf(searchTerm) !== -1) {
        for (const item of items) {
          termFound[getId(item)] = item;
        }
      }
    }
    if (!firstRound) {
      found = _.pickBy(found, (item, id) => termFound[id]);
    }
    firstRound = false;
  }

  return _.values(found);
}

export function schemaFilter(index, collection, filters, fields, getId, state = undefined) {
  if (!filters || _.isEmpty(collection)) {
    return collection;
  }

  const stringFilter = index && _.find(filters, _.isString);
  const stringTerms = stringFilter && stringFilter.split(" ").filter(_.identity);
  let filterResult = !_.isEmpty(stringTerms)
        ? schemaSearchIndexMultiple(index, getId, stringTerms)
        : collection;

  const otherFilters = _.difference(filters, [stringFilter]);
  otherFilters.forEach(filter => {
    let terms, fieldNames, schema;
    if (_.isString(filter)) {
      terms = filter.toLowerCase().split(" ").map(s => s.toLowerCase());
      fieldNames = _.keys(fields).filter(f => {
        const fb = fields[f].filterbox;
        return !fb || fb === "text" || fb.choices;
      });
    } else {
      schema = fields[filter.field];
      fieldNames = [filter.field];
      terms = [filter.criteria];
    }
    terms.forEach(term => {
      filterResult = filterResult.filter(item => _.some(fieldNames, fieldName => {
        const desc = fields[fieldName];
        if (!desc) {
          return false;
        }
        if (desc.subquery) {
          const { generator, inject, fields: subfields } = desc.subquery;
          let generated = injectGenerator(generator, inject, state)(item);
          if (_.isEmpty(generated)) {
            if (_.isArray(term)) {
              return _.isNull(term[0]);
            }
            return _.isNull(term);
          }
          if (!_.isArray(generated)) {
            generated = [generated];
          }
          const subqueryTerm = _.isString(filter)
            ? [term]
            : [{
              criteria: term,
              field: filter.field,
            }];
          return schemaFilter(index, generated, subqueryTerm, subfields, getId, state).length > 0;
        }
        let value = item[desc.alias || fieldName];
        if (schema && schema.filterbox === "date") {
          if (term.empty) {
                            // Suche nach leerem Datum erlauben
            return value === null || value === "";
          }

          if (value) {
            value = new moment(value).local().format("YYYY-MM-DD");
          }

          const termdate = term.today ? new Date().toISOString() : term.date;
                        // strip time information if present
          const td = termdate && termdate.substr(0, 10);
          const vd = value && value.substr(0, 10);
                        // ISO dates may be compared lexicographically as strings
          switch (term.comparison) {
            case "<": return vd && td && (vd < td);
            case ">": return !vd || (td && (vd > td));
            case "=": return vd && td && (td == vd);
            default: console.assert(false && term.comparison);
          }
        } else if (schema && schema.filterbox === "number") {
          switch (term.op) {
            case "=": return _.isNumber(value) && (_.isUndefined(term.equalTo) || Math.abs(value - term.equalTo) < 0.0001);
            case "><": return _.isNumber(value) && (_.isUndefined(term.atLeast) || value >= term.atLeast)
                                && (_.isUndefined(term.atMost) || value <= Math.max(term.atMost, _.isUndefined(term.atLeast) ? term.atMost : term.atLeast));
            case "!": return !_.isNumber(value);
            default: console.assert(false && term.op);
          }
        } else if (_.isString(term)) {
          if (_.isNumber(value)) {
            value = value.toString();
          }
          return (_.isString(value) && _.deburr(value.toLowerCase()).indexOf(_.deburr(term.toLowerCase())) >= 0) || (!term && (!value === !schema));
        } else if (_.isArray(term)) {
          return _.isEmpty(term)
                            || _.some(term, t => t === value)
                            || (_.isNull(term[0]) && !value);
        } else {
          return !!value === !!term;
        }
      }));
    });
  });
  return filterResult;
}

export function schemaSort(collection, sortBy, sortOpts, state) {
  const selectors = sortBy.map(({ key: sortKey }) => item => {
    let value = item[sortKey];
    const { selector, sortSelector, inject } = sortOpts[sortKey];
    if (sortSelector) {
            // Dedizierter Selector für den Sortierwert
      value = injectGenerator(sortSelector, inject, state)(value, item);
    } else if (selector) {
            // Standardselector für Sortierung verwenden (Mapping von ID zu Langtext
      value = injectGenerator(selector, inject, state)(value, item);
    }
    if (_.isDate(value)) {
      return value.getTime();
    }
    if (_.isString(value)) {
      return _.deburr(value.toLowerCase());
    }
    return value;
  });

  const orders = sortBy.map(({ reverse }) => reverse ? "desc" : "asc");

  const sortedResources = _.orderBy(collection, selectors, orders);

  return sortedResources;
}

export function generateFilters(schema) {
    // Filter aus Schemainfo übernehmen bzw. generieren
  if (schema.table) {
    if (!schema.filters) {
      schema.filters = {};
    }
    schema.table.map(prop => {
      if (!schema.filters.hasOwnProperty(prop.field)) {
        if (prop.hasOwnProperty("filter")) {
                    // Angaben aus Filter-Property in der Table
          if (!prop.filter.hide) {
            schema.filters[prop.field] = prop.filter;
            prop.filter.display = prop.filter.display || prop.display; // Wenn display nicht geändert Tabellenspalte verwenden
          }
        } else {
                    // Autogenerierter Filter aus Metadaten in der Table
          const gen = {
            display: prop.display,
            filterbox: (() => {
              switch (prop.type) {
                case "date": return "date";
                case "checkbox": return "boolean";
                case "number":
                case "money":
                  return "number";
                default: return "text";
              }
            })(),
          };

          gen.numeralFormat = prop.numeralFormat; // Format aus Table übernehmen
          schema.filters[prop.field] = gen;
        }
      }
    });
  }
}
